import React from "react";

function TransactionTable({ title, transactions, paymentMethods = [], showActions = false, onCancel }) {
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending:   { color: "bg-yellow-500", text: "Pending" },
      approved:  { color: "bg-green-500",  text: "Approved" },
      rejected:  { color: "bg-red-500",    text: "Rejected" },
      allowed:   { color: "bg-blue-500",   text: "Allowed" },
      cancelled: { color: "bg-gray-500",   text: "Cancelled" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color} text-white`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return isNaN(d.getTime())
      ? "Invalid Date"
      : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // 👇 Show the Payment Proof column only if at least one row has a screenshot URL
  const showProofColumn = Array.isArray(transactions) && transactions.some(t => !!t?.screenshotUrl);

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>

      {!Array.isArray(transactions) || transactions.length === 0 ? (
        <p className="text-gray-400 text-center py-8">No {title.toLowerCase()} found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-500 border-collapse">
            <thead>
              <tr className="bg-primary-dark text-gray-300">
                <th className="border border-gray-800 px-4 py-2 text-left">Date</th>
                <th className="border border-gray-800 px-4 py-2 text-left">Method</th>
                <th className="border border-gray-800 px-4 py-2 text-left">Amount</th>
                <th className="border border-gray-800 px-4 py-2 text-left">Reference</th>
                <th className="border border-gray-800 px-4 py-2 text-left">Status</th>
                {showProofColumn && (
                  <th className="border border-gray-800 px-4 py-2 text-left">Payment Proof</th>
                )}
                {showActions && (
                  <th className="border border-gray-800 px-4 py-2 text-left">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => {
                // Normalize payment method id & lookup
                const methodId =
                  transaction.paymentMethod?._id ??
                  transaction.paymentMethod ??
                  transaction.method;
                const method = paymentMethods.find((m) => m._id === methodId);

                const canCancel = transaction.status === "pending";
                const amount = Number(transaction.amount ?? 0);

                return (
                  <tr key={transaction._id || transaction.id} className="text-gray-300 border border-gray-700">
                    {/* Date */}
                    <td className="border border-gray-700 px-4 py-2">
                      {formatDate(transaction.createdAt || transaction.date)}
                    </td>

                    {/* Payment Method */}
                    <td className="border border-gray-700 px-4 py-2">
                      {method ? (
                        <div className="flex items-center space-x-2">
                          <span>{method.logo}</span>
                          <span>{method.name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">
                          {transaction.paymentMethod?.name || "Unknown Method"}
                        </span>
                      )}
                    </td>

                    {/* Amount */}
                    <td className="border border-gray-700 px-4 py-2">
                      ${amount.toFixed(2)}
                    </td>

                    {/* Reference */}
                    <td className="border border-gray-700 px-4 py-2 font-mono text-sm">
                      {transaction.reference || "N/A"}
                    </td>

                    {/* Status */}
                    <td className="border border-gray-700 px-4 py-2">
                      <StatusBadge status={transaction.status} />
                      {transaction.rejectionReason && transaction.status === "rejected" && (
                        <div className="text-xs text-red-400 mt-1">
                          Reason: {transaction.rejectionReason}
                        </div>
                      )}
                    </td>

                    {/* Payment Proof (view-only for users) */}
                    {showProofColumn && (
                      <td className="border border-gray-700 px-4 py-2">
                        {transaction.screenshotUrl ? (
                          <button
                            onClick={() => window.open(transaction.screenshotUrl, "_blank")}
                            className="text-blue-400 hover:text-blue-300 underline text-sm font-medium transition-colors"
                          >
                            View Proof
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">No proof</span>
                        )}
                      </td>
                    )}

                    {/* Actions */}
                    {showActions && (
                      <td className="border border-gray-700 px-4 py-2">
                        {canCancel ? (
                          <button
                            onClick={() => onCancel?.(transaction._id || transaction.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition"
                          >
                            Cancel
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">No action required</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TransactionTable;
